import { InfoCircleOutlined } from '@ant-design/icons'
import { useFormik } from 'formik'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Form, FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { CustomFields } from 'features/project-form/components'
import { FormTitle } from 'features/project-form/components/FormTitle'
import { Product } from 'features/project-form/features/product/types'
import { ProductSchema } from 'features/project-form/features/product/validators'
import { generateProductId } from 'features/project-form/utils'

type Props = {
  onDelete?: () => void
  addProduct: (product: Product) => void
  productToEdit: Product | null
  handleOpenProject: () => void
  setProductToEditIndex: (n: number | null) => void
  deleteProduct: () => void
}

const initialValues: Product = {
  id: 0,
  name: '',
  brand: null,
  general: false,
  hierarchy: null,
  custom: {},
}

const layout = { labelCol: { span: 5 } }
export const ProductForm: React.FC<Props> = ({
  onDelete,
  addProduct,
  handleOpenProject,
  productToEdit,
  setProductToEditIndex,
}) => {
  const formik = useFormik({
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: ProductSchema,
    onSubmit: (values) => {
      addProduct({ ...values, id: values.id ? values.id : generateProductId() })
      handleOpenProject()
    },
  })

  useEffect(() => {
    if (productToEdit && !formik.touched.name) {
      formik.setTouched({ name: true })

      formik.setFieldValue('name', productToEdit.name)
      formik.setFieldValue('brand', productToEdit.brand || '')
      formik.setFieldValue('hierarchy', productToEdit.hierarchy || '')
      formik.setFieldValue('general', productToEdit.general || false)
      formik.setFieldValue('id', productToEdit.id)
      formik.setFieldValue('custom', productToEdit.custom)
    }

    return () => {
      setProductToEditIndex(null)
    }
  }, [productToEdit, formik, setProductToEditIndex])

  return (
    <Wrapper>
      <Form
        layout="horizontal"
        {...layout}
        onFinish={() => formik.submitForm()}
      >
        <FormTitle>New Product</FormTitle>
        <FormItem
          label={
            <Tooltip content="Give your product item (e.g. SKU) a name to display in the charts. Make sure it's one you'll recognize.">
              <span>
                Item Name <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
          validateStatus={formik.errors.name ? 'error' : ''}
          help={formik.errors.name}
          wrapperCol={{ span: 10 }}
        >
          <Input
            $error={!!formik.errors.name}
            name="name"
            onChange={formik.handleChange}
            value={formik.values.name}
            placeholder="Insert product name here"
          />
        </FormItem>
        <FormItem
          label={
            <Tooltip content="Provide the name of the item's brand to display in the charts.">
              <span>
                Brand <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
          validateStatus={formik.errors.brand ? 'error' : ''}
          help={formik.errors.brand}
          wrapperCol={{ span: 10 }}
        >
          <Input
            $error={!!formik.errors.brand}
            name="brand"
            onChange={formik.handleChange}
            value={formik.values.brand || ''}
            placeholder="Insert product formal name here"
          />
        </FormItem>
        <FormItem
          label={
            <Tooltip content="Provide the name of the overall umbrella product that should contain this item.">
              <span>
                Product <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
          validateStatus={formik.errors.hierarchy ? 'error' : ''}
          help={formik.errors.hierarchy}
          wrapperCol={{ span: 10 }}
        >
          <Input
            $error={!!formik.errors.hierarchy}
            name="hierarchy"
            onChange={formik.handleChange}
            value={formik.values.hierarchy || ''}
            placeholder="Insert product here"
          />
        </FormItem>
        <FormItem
          label={
            <Tooltip content='Indicate whether this item is the "generic" catch-all item for the specified product. Syndicated reviews that cannot be explicitly tied to a particular item will be placed under this general item.'>
              <span>
                General Item <InfoCircleOutlined />
              </span>
            </Tooltip>
          }
          wrapperCol={{ span: 10 }}
        >
          <Checkbox
            name="general"
            checked={formik.values.general}
            onChange={(checked) => formik.setFieldValue('general', checked)}
          />
        </FormItem>
        <CustomFields
          fields={formik.values.custom}
          onFieldAdd={(field: string, value: string) =>
            formik.setFieldValue(`custom`, {
              ...formik.values.custom,
              [field]: value,
            })
          }
          onFieldRemove={(field: string) => {
            const values = { ...formik.values.custom } as Record<string, string>
            if (values) {
              delete values[field]
            }
            formik.setFieldValue(`custom`, values)
          }}
        />
        <ButtonsWrapper>
          {onDelete && (
            <SubmitButton variant="secondary" onClick={onDelete}>
              Delete
            </SubmitButton>
          )}
          <SubmitButton variant="primary" type="submit">
            {onDelete ? 'Save' : 'Add Product'}
          </SubmitButton>
        </ButtonsWrapper>
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-height: 800px;
  width: 800px;
  overflow: auto;
`
const ButtonsWrapper = styled.div`
  margin-top: auto;
  margin-bottom: 0;
  display: flex;
  justify-content: center;
`
const SubmitButton = styled(Button)`
  margin: 40px 20px 0;
`
